import {
  QueryCache,
  QueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';

// Requests torn down before a response (tab close, navigation, offline)
const ABORTED_CODES = ['ERR_CANCELED', 'ERR_NETWORK', 'ECONNABORTED'];

// Services wrap AxiosErrors in friendly messages and keep the original as cause
const getAxiosError = (error: Error) => {
  if (isAxiosError(error)) return error;
  if (isAxiosError(error.cause)) return error.cause;
  return undefined;
};

export const shouldReportQueryError = (error: Error) => {
  const axiosError = getAxiosError(error);
  if (!axiosError) return true;
  // 4xx are expected (auth/permissions); the API doesn't report HttpException 5xx
  if (axiosError.response) return axiosError.response.status >= 500;
  return !ABORTED_CODES.includes(axiosError.code ?? '');
};

// Create a client
export default new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!shouldReportQueryError(error)) return;
      Sentry.captureException(error, {
        tags: { source: 'react-query' },
        extra: { queryKey: query.queryKey },
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * (1000 * 60),
      retry: false,
      placeholderData: keepPreviousData,
    },
  },
});
